/**
 * Handoff — Prototype 01c (State 3)
 * Route: /prototypes/handoff
 *
 * Damon has finished. He introduced someone but did not name them. That
 * "someone" becomes recognizable now — not because Toto appears, but
 * because Presence begins to be noticed.
 *
 * The awakening is not an appearance. Presence was on the screen the
 * whole time as a small resting mark. Now it grows into a rest form
 * proportional to the moment, warms slightly, and remains still. Nothing
 * bounces. Nothing celebrates. This is Law #1 in practice: Presence is
 * never created — only recognized.
 *
 * Toto's line arrives beside Presence, not as a chat bubble, not as a
 * modal — as text that seems to come from the substance itself. It is
 * short. It is a taking-of-responsibility.
 *
 * From here, the next click would open the Sonmi Introduction prototype
 * (Prototype 02). This handoff is the threshold, not the introduction.
 */
import React, { useState } from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";

export default function Handoff() {
  // Two sub-states shown side by side for review — this prototype needs to
  // show both "before Presence is noticed" and "after it awakens".
  const [awake, setAwake] = useState(true);

  const notes = {
    communicates:
      "The moment of transfer. Damon has finished. A guide steps forward — but not as a new entity arriving. As something that was present all along, becoming recognizable now that the conversation is with it. Toto assumes responsibility for the rest of the experience. Katrina should feel that she's still in the same room, with the same substance, only with a new person speaking.",
    placeholder: [
      "'I'll take it from here.' is placeholder copy. Final line is a writing task and subject to review.",
      "Damon's card is shown in its 'ended' state — the video finished. In final, this may be a still frame from the video's last second, or a quiet portrait, rather than the placeholder rectangle.",
      "Presence's 'awakening' here is represented by a size/tone shift only. The final motion should be very slight — a becoming-noticed, not a growth animation. The toggle at the top of the prototype lets you see both frames.",
      "The 'Continue' affordance is intentionally minimal. In final delivery, Presence may simply proceed on its own after a pause, without a button.",
    ],
    decisions: [
      "Should Presence be noticeably larger than its resting mark, or the same size but warmer? Currently: modestly larger (from 44px to 120px) AND warmer. Consider: smaller change might be truer to 'noticed, not appeared.'",
      "Should Toto's line appear as text beside Presence, or should Presence itself carry the words (e.g., text emerging from within its glow)? Currently: text beside. Alternative feels more coherent but harder to review.",
      "Does the Damon card remain visible on this screen, or fully dissolve once Presence is noticed? Currently: remains, quieted. Argument for keeping: this is a handoff, not a replacement; his presence should still be felt.",
      "Should the 'Continue' action reveal the label 'Introduction' or simply an arrow? Currently: 'Continue' — most neutral.",
    ],
    assumptions: [
      "Presence's awakening is the boundary between 'normal software' and 'the demonstration.' Everything after this point is a different register.",
      "Katrina already understands from Damon's line ('I'd like to introduce you to someone') that she is about to meet someone new. Presence becoming noticeable is the answer to that setup.",
      "Toto's first words are a taking-of-responsibility, not a self-introduction. He doesn't say 'I'm Toto.' He assumes his role.",
    ],
  };

  return (
    <PrototypeShell title="Prototype 01 · Handoff (state 3 — Toto arrives)" notes={notes}>
      <section className="max-w-3xl mx-auto px-8 lg:px-12 pt-6 pb-24" data-testid="handoff">
        {/* Frame toggle — a review affordance, not a final control */}
        <div className="mb-6 flex items-center gap-3 text-[11px] text-stone-500" data-testid="handoff-frame-toggle">
          <span>Preview:</span>
          <button
            onClick={() => setAwake(false)}
            className={`px-3 py-1 rounded-full ${!awake ? "bg-stone-900 text-white" : "subtle-border bg-white text-stone-600"}`}
            data-testid="handoff-frame-before"
          >
            just as Damon ends
          </button>
          <button
            onClick={() => setAwake(true)}
            className={`px-3 py-1 rounded-full ${awake ? "bg-stone-900 text-white" : "subtle-border bg-white text-stone-600"}`}
            data-testid="handoff-frame-after"
          >
            Presence noticed
          </button>
        </div>

        {/* Damon's card — in its "ended" state, quieted but still present */}
        <div className="rounded-2xl bg-white subtle-border overflow-hidden opacity-70" data-testid="handoff-damon-ended">
          <div className="aspect-video bg-stone-100 flex items-center justify-center text-stone-400 text-xs italic">
            [Damon's message · ended]
          </div>
          <div className="px-6 py-4">
            <div className="text-[13px] text-stone-500">Damon · General Manager, Plantation</div>
          </div>
        </div>

        {/* Presence + Toto's line */}
        <div className="mt-16 flex items-center gap-8" data-testid="handoff-presence-block">
          <div className="shrink-0 transition-all duration-1000">
            <PresenceForm
              size={awake ? 120 : 44}
              tone={awake ? "warm" : "neutral"}
              testId="handoff-presence"
            />
          </div>
          <div className="flex-1 min-w-0">
            {awake ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400">Toto</div>
                <p
                  className="font-serif-display text-3xl lg:text-4xl text-stone-900 mt-2 leading-snug"
                  style={{ fontWeight: 400 }}
                  data-testid="handoff-toto-line"
                >
                  I'll take it from here.
                </p>
              </>
            ) : (
              <p className="text-sm text-stone-400 italic">
                (Damon's message has just ended. Presence has not yet been noticed. This is the pause before the handoff.)
              </p>
            )}
          </div>
        </div>

        {/* Continue affordance — visible only in the awake frame */}
        {awake && (
          <div className="mt-16" data-testid="handoff-continue-row">
            <button
              className="px-6 py-2.5 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800"
              data-testid="handoff-continue"
              onClick={(e) => e.preventDefault()}
            >
              Continue
            </button>
            <div className="text-[11px] text-stone-400 italic mt-3">
              (prototype — action is inert. In the flow, this would open the Sonmi introduction.)
            </div>
          </div>
        )}
      </section>
    </PrototypeShell>
  );
}
